/**
 * §F Competitive Set — chip-based review surface (Direction 4: selectable chips).
 *
 * In CONTROLLED mode (an explicit `value`/`onChange` pair) every mutation flows
 * straight out through `onChange`, and nothing is fetched here directly — that
 * contract is what keeps this component testable in isolation (see the suite in
 * `__tests__/CompetitiveSetSection.test.tsx`, all of which render controlled).
 * `value`/`onChange` are OPTIONAL: when the caller doesn't supply them, this
 * component falls back to `useCompetitiveSet` (./useCompetitiveSet.ts), the
 * §F-owned hook that reads and writes bb_chunks rows through the wizard
 * store's existing `upsertEntry`/`discardEntry`. Persistence is the default;
 * an explicit `value`/`onChange` is the escape hatch for tests and any
 * parent that wants to own the state itself.
 *
 * EXPLICIT-SAVE CONTRACT (persisted mode only — §F is in CONVERTED_SECTIONS):
 * add / edit position_note / edit our_angle / remove (the x) are EDITS that
 * BUFFER id-keyed in section-local state (see CompetitorBuffer below); nothing
 * reaches the store until the section Save button fires the registered `save()`,
 * which commits the buffer through the SAME `upsertEntry`/`discardEntry` paths
 * via the hook's `commitBuffered`. `discard()` restores from the store.
 * Confirm/Reject are OPERATOR ACTIONS, not edits — they stay IMMEDIATE (the
 * hook's `confirmMany`/`rejectMany`) and never touch the buffer. Buffered
 * removes are id-keyed (an explicit remove of a known id) and NEVER synthesized
 * by diffing list contents — the §B prod data-loss ruling. Controlled mode is
 * fully unbuffered and does not register with the save registry.
 *
 * REVIEW MODEL — each record renders ONCE, as a chip:
 *
 *   1. GROUP BY SOURCE. Chips are grouped into "Suggested automatically"
 *      (source === 'discovered') and "Entered by a person" (everything else
 *      — quick-add/bulk-paste always create 'client_edit' records, and
 *      'intake' records were typed in by a person). This makes machine-
 *      suggested records visible as their own cohort, and a shift-click
 *      selection range can never span the two groups.
 *   2. FILTER PILLS. "Needs review N" / "Confirmed N" / "All N" slice both
 *      groups by review state, with live counts.
 *   3. SELECTION. Hover (or keyboard focus) turns a chip's leading
 *      placeholder-mark slot into a checkbox — the mark is a plain initial
 *      tile; NO favicon is ever fetched, that would be one outbound request
 *      per row. Clicking the checkbox toggles selection and reveals a bulk
 *      action bar with a LIVE COUNT; clicking the chip label expands that
 *      record inline; shift-click range-selects STRICTLY within the current
 *      group AND current filter; the x removes the record (unchanged).
 *   4. BULK BAR. "Confirm N" and "Not competitors (N)" always name the
 *      count. There is deliberately no select-all or bare verify-all
 *      shortcut — selection is the consent.
 *   5. EXPAND IN PLACE. The opened record shows position_note and
 *      our_angle, its provenance chips, then "Confirm competitor" /
 *      "Not a competitor" / "Next unreviewed".
 *   6. REJECT IS FIRST-CLASS at single and bulk scale. In controlled mode
 *      (explicit value/onChange) reject is expressed as removal through
 *      onChange — the presentational record shape has no rejected field.
 *      In persisted mode it delegates to the hook's `rejectMany`, which owns
 *      what a rejection durably persists (see useCompetitiveSet.ts).
 *
 * Standing rule this section exists to enforce: competitor intelligence is
 * internal by default. Every record is created with `public_mention: false`
 * and CANNOT be flipped to `true` from this UI at all. Two backend facts
 * make any "verify, then confirm an exception" flow dishonest rather than
 * merely gated:
 *
 *   1. `backend/api/services/brand_bible_v2/entries.py` force-resets
 *      `public_mention` to `False` for every `bb_chunks` row with
 *      `kind === "competitor"`, on both the create path (~L168-171) and the
 *      patch path (~L244-245), unconditionally — there is no exception path.
 *      A write from here always gets silently discarded server-side.
 *   2. The old gate required `record.verified === true`, but nothing in this
 *      component (or its persistence hook) ever calls a verify endpoint, so
 *      that flag can never legitimately become true from §F.
 *
 * The old permanently-disabled public-mention switch left with the dense
 * record list this surface replaced: a control that can only ever lie is
 * worse than none, and the internal-only rule is now communicated by the
 * banner plus each record's "internal only" provenance chip instead.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Clipboard, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { cleanDomain, isValidDomain } from './CompetitorsInput';
import { useCompetitiveSet, type CompetitiveBufferedCommit } from './useCompetitiveSet';
// Imported from the save module directly (not the ./save barrel's re-export path
// via any brand-bible barrel) to keep §F out of an import cycle, same discipline
// as the AISuggest import below.
import {
  useRegisterBbSectionSave,
  type BbSectionSaveRegistration,
  type DirtyFormSaveResult,
} from './save/BbSectionSaveContext';
// Imported directly (not via ./index) so this module never depends on the
// barrel that re-exports it — keeps the §F component out of an import cycle.
import { AISuggest } from './AISuggest';
import { requestModelFieldSuggestions } from '@/services/brandBibleAiSuggest';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import {
  COMPETITOR_FIELD_COVERAGE,
  buildCompetitorSuggestRequest,
  classifySuggestError,
  competitorDraftCard,
  competitorNotesSectionId,
  mapCompetitorSuggestResponse,
  readinessRefusals,
  resolveCompetitorField,
  type CompetitorAIField,
} from './competitiveSetAiSuggest';

export interface CompetitorRecord {
  id: string;
  name: string;
  domain?: string;
  position_note?: string;
  our_angle?: string;
  public_mention: boolean;
  source?: 'client_edit' | 'intake' | 'discovered';
  verified?: boolean;
  last_verified?: string;
}

/**
 * The tenant's OWN brand facts, used only to ground the `our_angle` AI draft
 * (how WE position against a competitor — a claim about US, never about them).
 *
 * OPTIONAL with a safe empty default. When absent (or empty), `our_angle` is
 * sent as a grounded target with no tenant_context strengths and the operator
 * sees a refusal rather than an invented angle. The parent that renders §F
 * (BrandBiblePage) passes this through from the brand draft.
 */
export interface CompetitorBrandContext {
  businessName?: string;
  description?: string;
  voice?: string;
  offers?: string;
  differentiators?: string;
}

export interface CompetitiveSetSectionProps {
  /**
   * Optional as of the §F persistence wiring: omit both `value` and
   * `onChange` to let `useCompetitiveSet` own state (reads/writes bb_chunks
   * via the wizard store). Supply both to keep this component controlled —
   * e.g. every test in `__tests__/CompetitiveSetSection.test.tsx` does this,
   * and that isolation contract must keep working.
   */
  value?: CompetitorRecord[];
  onChange?: (next: CompetitorRecord[]) => void;
  disabled?: boolean;
  /**
   * The tenant's own brand facts — the ONLY source the `our_angle` AI draft is
   * allowed to draw on. Optional with a safe empty default; when absent the
   * angle draft is withheld (see CompetitorBrandContext above). Never used to
   * draft anything about the competitor.
   */
  brandContext?: CompetitorBrandContext;
  /**
   * The tenant's own primary domain. Purely structural, self-domain guard:
   * a tenant cannot add its own site as its own competitor. Optional — when
   * omitted, no self-domain check runs and quick-add/bulk-paste behave
   * exactly as before this prop existed. Scoped to the primary host only —
   * no alias/redirect resolution, since no alias storage exists anywhere in
   * this codebase.
   */
  tenantDomain?: string;
}

const STALE_DAYS = 180;

const SOURCE_LABEL: Record<NonNullable<CompetitorRecord['source']>, string> = {
  client_edit: 'client edit',
  intake: 'intake',
  discovered: 'discovered',
};

/**
 * Source grouping (review-model item 1). 'discovered' records are machine-
 * suggested; 'client_edit' and 'intake' records were both typed in by a
 * person (quick-add / bulk-paste / intake form), so they share the second
 * group. An undefined source is treated as person-entered — the only paths
 * that create records without a source are the person-driven ones.
 */
type GroupKey = 'suggested' | 'person';
const GROUP_ORDER: GroupKey[] = ['suggested', 'person'];
const GROUP_LABEL: Record<GroupKey, string> = {
  suggested: 'Suggested automatically',
  person: 'Entered by a person',
};

function groupOf(record: CompetitorRecord): GroupKey {
  return record.source === 'discovered' ? 'suggested' : 'person';
}

/** Filter pills (review-model item 2): review state across both groups. */
type FilterKey = 'review' | 'confirmed' | 'all';

function matchesFilter(record: CompetitorRecord, filter: FilterKey): boolean {
  if (filter === 'all') return true;
  return filter === 'confirmed' ? record.verified === true : record.verified !== true;
}

/**
 * Bulk review operations the persistence hook exposes in uncontrolled mode.
 * Declared locally with every member optional so this component compiles
 * against the hook's stable base export (`records`/`onChange`) whether or
 * not the extended surface is present yet; controlled mode (explicit
 * value/onChange) never consults these and expresses the same operations
 * through `onChange` instead (see confirmRecords/rejectRecords below).
 */
type BulkReviewOps = {
  confirmMany?: (ids: string[]) => void;
  rejectMany?: (ids: string[]) => void;
};

type QuickCandidate = { name: string; domain?: string };

/* --------------------------- explicit-save buffer --------------------------- */

/**
 * §F explicit-save buffer (persisted mode only). Deliberately ID-KEYED, never a
 * whole-list snapshot: `added` holds temp-id records to create, `edits` maps an
 * existing store row's id to its desired next record, and `removed` is the
 * explicit set of ids to soft-delete. Per the §B prod data-loss ruling, a
 * removal is ONLY ever an explicit remove of a known id — nothing here is
 * derived by diffing list contents, so a dropped or filtered list can never
 * manufacture a delete. Confirm/Reject are operator actions, not edits: they
 * stay immediate and never touch this buffer.
 */
interface CompetitorBuffer {
  added: CompetitorRecord[];
  edits: Record<string, CompetitorRecord>;
  removed: string[];
}

const EMPTY_BUFFER: CompetitorBuffer = { added: [], edits: {}, removed: [] };

/** Real count of buffered changes: adds + edits + removes. */
function bufferChangeCount(b: CompetitorBuffer): number {
  return b.added.length + Object.keys(b.edits).length + b.removed.length;
}

/** Project the store's base records through the buffer into the view the UI shows. */
function applyBuffer(base: CompetitorRecord[], b: CompetitorBuffer): CompetitorRecord[] {
  const removed = new Set(b.removed);
  const view = base.filter((r) => !removed.has(r.id)).map((r) => b.edits[r.id] ?? r);
  return [...view, ...b.added];
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cset_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Dedupe key: normalized domain when present, else case-insensitive name — per spec §3. */
function dedupeKey(rec: QuickCandidate): string {
  const d = rec.domain ? cleanDomain(rec.domain) : '';
  if (d) return `d:${d}`;
  return `n:${rec.name.trim().toLowerCase()}`;
}

/**
 * Structural self-domain guard (spec: "Import gate is structural, role-based
 * — no coded industry lists"). The only role-based rule in scope: a tenant
 * cannot add its own primary host as its own competitor. Both sides run
 * through the same `cleanDomain` normalizer already used for the dedupe key,
 * so scheme/`www.`/path/case differences all collapse to the same value.
 */
function normalizeTenantDomain(tenantDomain?: string): string {
  return tenantDomain ? cleanDomain(tenantDomain) : '';
}

function isSelfDomain(candidateDomain: string | undefined, normalizedTenant: string): boolean {
  if (!normalizedTenant || !candidateDomain) return false;
  return cleanDomain(candidateDomain) === normalizedTenant;
}

function isStale(lastVerified?: string): boolean {
  if (!lastVerified) return false;
  const t = new Date(lastVerified).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t > STALE_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Quick-add is permissive: if the text parses as a valid domain (via the
 * existing CompetitorsInput helpers), use it for both name and domain;
 * otherwise it's just a name. Quick-add never rejects on shape — only on
 * duplicate (handled by the caller) — a bare name is a legitimate entry.
 */
function parseQuickAdd(raw: string): QuickCandidate | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const asDomain = cleanDomain(trimmed);
  if (asDomain && isValidDomain(asDomain)) {
    return { name: asDomain, domain: asDomain };
  }
  return { name: trimmed };
}

type BulkStatus = 'valid' | 'duplicate' | 'invalid' | 'self';
interface BulkPreviewItem {
  raw: string;
  parsed: QuickCandidate | null;
  status: BulkStatus;
}

/** A line only lands in "invalid" when it looks like a domain the author meant
 * to paste (has a dot / a protocol) but doesn't clean up into one — a plain
 * name never fails this bucket, since names have no shape to violate. */
function looksLikeDomainAttempt(raw: string): boolean {
  return /^https?:\/\//i.test(raw) || (/\.[a-z]{2,}/i.test(raw) && !/\s/.test(raw));
}

function buildBulkPreview(text: string, existing: CompetitorRecord[], normalizedTenant: string): BulkPreviewItem[] {
  const lines = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  const existingKeys = new Set(existing.map(dedupeKey));
  const seenInBatch = new Set<string>();
  return lines.map((raw) => {
    if (looksLikeDomainAttempt(raw)) {
      const d = cleanDomain(raw);
      if (!d || !isValidDomain(d)) {
        return { raw, parsed: null, status: 'invalid' as const };
      }
      if (isSelfDomain(d, normalizedTenant)) {
        return { raw, parsed: { name: d, domain: d }, status: 'self' as const };
      }
      const parsed: QuickCandidate = { name: d, domain: d };
      const key = dedupeKey(parsed);
      if (existingKeys.has(key) || seenInBatch.has(key)) return { raw, parsed, status: 'duplicate' as const };
      seenInBatch.add(key);
      return { raw, parsed, status: 'valid' as const };
    }
    const parsed: QuickCandidate = { name: raw };
    const key = dedupeKey(parsed);
    if (existingKeys.has(key) || seenInBatch.has(key)) return { raw, parsed, status: 'duplicate' as const };
    seenInBatch.add(key);
    return { raw, parsed, status: 'valid' as const };
  });
}

/* --------------------------------- NoteField --------------------------------- */

function NoteField({
  label,
  placeholder,
  value,
  editing,
  disabled,
  onEdit,
  onCommit,
}: {
  label: string;
  placeholder: string;
  value: string;
  editing: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onCommit: (v: string) => void;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold text-muted-foreground">{label}</div>
      {editing ? (
        <textarea
          autoFocus
          defaultValue={value}
          disabled={disabled}
          rows={2}
          onBlur={(e) => onCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') e.currentTarget.blur();
          }}
          className="w-full resize-y rounded-lg border border-coral bg-card px-2.5 py-1.5 text-[12.5px] outline-none ring-[3px] ring-coral/15"
        />
      ) : (
        <p
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && onEdit()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onEdit();
            }
          }}
          className={cn(
            'min-h-[28px] cursor-text rounded-lg border border-transparent px-2.5 py-1.5 text-[12.5px] leading-snug hover:border-border',
            value ? 'text-foreground' : 'italic text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-60 hover:border-transparent',
          )}
        >
          {value || placeholder}
        </p>
      )}
    </div>
  );
}

/* ----------------------------- ConfirmPublicMention ---------------------------- */

/**
 * UNREACHABLE — kept for later reuse, not wired to any call site.
 *
 * This dialog used to gate turning `public_mention` on. It's deliberately
 * disconnected: the backend discards `public_mention: true` for every
 * competitor chunk unconditionally (see the file-header comment), so a
 * confirm-and-write flow here can only ever produce a write the server
 * throws away — worse than no control at all. If a future backend adds a
 * real approve-for-public path for competitor chunks, this component is a
 * reasonable starting point for the "explicit exception" UX, but it needs a
 * new call site wired to that path, not the old direct `onUpdate`.
 */
function ConfirmPublicMention({
  name,
  onCancel,
  onConfirm,
}: {
  name: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div onClick={onCancel} className="fixed inset-0 z-[80] grid place-items-center bg-foreground/30 backdrop-blur-sm">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[420px] max-w-[92vw] rounded-2xl border border-[hsl(var(--status-warning)/0.35)] bg-card shadow-2xl"
      >
        <div className="flex items-start gap-2.5 border-b border-border px-4 pb-3 pt-4">
          <AlertTriangle className="mt-0.5 size-4 flex-none text-[hsl(var(--status-warning))]" />
          <div>
            <div className="text-sm font-bold">Allow public mention of {name}?</div>
            <p className="mt-1 text-[12px] text-muted-foreground">
              The standing rule is internal-only: agents use this set to shape strategy and positioning, and never
              name a competitor in public copy. Turning this on is an explicit exception for this one record.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground hover:bg-warm-bg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg border border-[hsl(var(--status-warning))] bg-[hsl(var(--status-warning))] px-3.5 py-2 text-[13px] font-semibold text-[hsl(var(--status-warning-foreground))] hover:opacity-90"
          >
            Allow public mention
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- CompetitorChip ------------------------------- */

/**
 * One competitor record as a selectable chip (review-model item 3).
 *
 * The leading slot is the neutral placeholder mark — a plain initial tile,
 * never a fetched favicon (an outbound request per row is the reason this
 * surface deliberately has no favicons). On hover or keyboard focus the
 * mark swaps to an empty checkbox; while selected it shows the checked box
 * permanently. The slot button also owns shift-click range selection (see
 * `onSlotClick` in the parent). The label button expands the record in
 * place; the x removes it (unchanged behaviour).
 */
function CompetitorChip({
  record,
  selected,
  expanded,
  disabled,
  onSlotClick,
  onToggleExpand,
  onRemove,
}: {
  record: CompetitorRecord;
  selected: boolean;
  expanded: boolean;
  disabled?: boolean;
  onSlotClick: (e: React.MouseEvent, record: CompetitorRecord) => void;
  onToggleExpand: (record: CompetitorRecord) => void;
  onRemove: (id: string) => void;
}) {
  const initial = (record.name.trim()[0] ?? '?').toUpperCase();
  return (
    <span
      className={cn(
        'group inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-1 text-[12.5px] font-medium transition-colors',
        selected ? 'border-coral/40 bg-coral-light text-coral-dark' : 'border-border bg-warm-bg text-foreground',
        expanded && 'ring-2 ring-coral ring-offset-1',
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={`Select ${record.name}`}
        disabled={disabled}
        onClick={(e) => onSlotClick(e, record)}
        className="grid size-5 flex-none place-items-center rounded-[5px] focus:outline-none focus-visible:ring-2 focus-visible:ring-coral disabled:cursor-not-allowed disabled:opacity-40"
      >
        {selected ? (
          <span className="grid size-4 place-items-center rounded-[4px] bg-coral text-white">
            <Check className="size-3" />
          </span>
        ) : (
          <>
            <span className="grid size-4 place-items-center rounded-[4px] bg-teal-light font-mono text-[10px] font-semibold uppercase text-teal group-hover:hidden group-focus-within:hidden">
              {initial}
            </span>
            <span className="hidden size-4 rounded-[4px] border border-border bg-card group-hover:block group-focus-within:block" />
          </>
        )}
      </button>
      <span
        title={record.verified ? 'Confirmed' : 'Needs review'}
        className={cn(
          'size-1.5 flex-none rounded-full',
          record.verified ? 'bg-[hsl(var(--status-success))]' : 'bg-[hsl(var(--status-warning))]',
        )}
      />
      <button
        type="button"
        aria-expanded={expanded}
        disabled={disabled}
        onClick={() => onToggleExpand(record)}
        className="min-w-0 truncate px-0.5 text-left disabled:cursor-not-allowed disabled:opacity-60"
      >
        {record.name}
        {record.domain && record.domain !== record.name && (
          <span className="ml-1 font-mono text-[10.5px] text-muted-foreground">{record.domain}</span>
        )}
      </button>
      <button
        type="button"
        aria-label={`Remove ${record.name}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(record.id);
        }}
        className={cn(
          'grid size-4 flex-none place-items-center rounded bg-transparent',
          selected ? 'text-coral-dark' : 'text-muted-foreground hover:text-destructive',
          'disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

/* --------------------------- field-coverage AI action -------------------------- */

export type { CompetitorAIField } from './competitiveSetAiSuggest';
export {
  COMPETITOR_AI_FIELDS,
  COMPETITOR_FIELD_COVERAGE,
  competitorNotesSectionId,
  hasBrandGrounding,
} from './competitiveSetAiSuggest';

/* ---------------------------- ExpandedRecordPanel ---------------------------- */

/**
 * The in-place expansion for one chip (review-model item 5): position_note
 * and our_angle editors, provenance chips, the ONE AI Suggest that posts the
 * shared A–I contract for all four fields (mapped per target_field), then the
 * single-record review actions. name/domain are refuse-only. Reject ("Not a
 * competitor") is first-class here, not a hidden path behind remove.
 */
function ExpandedRecordPanel({
  record,
  disabled,
  brandContext,
  tenantId,
  hasNextUnreviewed,
  onUpdate,
  onConfirm,
  onReject,
  onNextUnreviewed,
}: {
  record: CompetitorRecord;
  disabled?: boolean;
  brandContext?: CompetitorBrandContext;
  tenantId: string | null;
  hasNextUnreviewed: boolean;
  onUpdate: (next: CompetitorRecord) => void;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onNextUnreviewed: (fromId: string) => void;
}) {
  const [editingField, setEditingField] = useState<'position_note' | 'our_angle' | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [serverRefusals, setServerRefusals] = useState<
    Array<{ field: CompetitorAIField; reason: string }> | null
  >(null);
  const stale = isStale(record.last_verified);
  const brand = brandContext ?? {};

  const readiness = useMemo(
    () =>
      readinessRefusals(record, brandContext ?? {}).map((r) => ({
        field: r.target_field as CompetitorAIField,
        reason: r.reason,
      })),
    [record, brandContext],
  );
  const refusals = serverRefusals ?? readiness;

  // Side-channel from the exact card string the user accepts back to the field
  // it belongs to. Keyed by the full display string (AISuggest passes each text
  // item back untouched) so acceptance routes by stored target_field — never by
  // parsing the label out of the text.
  const draftMapRef = useRef<Map<string, { field: CompetitorAIField; text: string }>>(new Map());

  const suggestFromModel = async ({ dismissed }: { existing: string[]; dismissed: string[] }) => {
    setFetchError(null);
    const request = buildCompetitorSuggestRequest({ record, brand, dismissed });
    try {
      const response = await requestModelFieldSuggestions(request, tenantId);
      const mapped = mapCompetitorSuggestResponse(response);
      setServerRefusals(
        mapped.refusals.map((r) => ({
          field: r.target_field as CompetitorAIField,
          reason: r.reason,
        })),
      );
      const map = new Map<string, { field: CompetitorAIField; text: string }>();
      const items: string[] = [];
      for (const draft of mapped.drafts) {
        const field = resolveCompetitorField(draft);
        if (!field || field === 'name' || field === 'domain') continue;
        const display = competitorDraftCard(draft);
        map.set(display, { field, text: draft.value });
        items.push(display);
      }
      draftMapRef.current = map;
      return { items, source: mapped.source };
    } catch (err) {
      setFetchError(classifySuggestError(err));
      setServerRefusals(null);
      throw err;
    }
  };

  const acceptFieldDraft = (display: string) => {
    const entry = draftMapRef.current.get(display);
    if (!entry) return;
    // Writes ONLY the accepted field on THIS record, through the same onUpdate
    // path a manual edit uses. Human acceptance is mandatory — nothing here
    // auto-applies; this only fires from the card's own "Use this" button.
    // name/domain are never accepted as AI drafts.
    if (entry.field === 'name' || entry.field === 'domain') return;
    if (entry.field === 'position_note') {
      onUpdate({ ...record, position_note: entry.text });
      return;
    }
    if (entry.field === 'our_angle') {
      onUpdate({ ...record, our_angle: entry.text });
    }
  };

  return (
    <div className="mt-1 flex basis-full flex-col gap-2.5 rounded-lg border border-border bg-card p-3.5">
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="truncate text-[13.5px] font-semibold">{record.name}</span>
        {record.domain && (
          <span className="truncate font-mono text-[11.5px] text-muted-foreground">{record.domain}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <span className="rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-teal">
          {SOURCE_LABEL[record.source ?? 'client_edit']}
        </span>
        <span
          className={cn(
            'rounded-[5px] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide',
            record.verified
              ? 'bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]'
              : 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]',
          )}
        >
          {record.verified ? 'confirmed' : 'needs review'}
        </span>
        <span className="rounded-[5px] border border-border bg-warm-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          internal only
        </span>
        {stale && (
          <span className="rounded-[5px] bg-[hsl(var(--status-warning-bg))] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--status-warning))]">
            stale
          </span>
        )}
      </div>

      <NoteField
        label="Position note — how they show up in this market"
        placeholder="Add what you know — positioning, claims, strengths…"
        value={record.position_note ?? ''}
        editing={editingField === 'position_note'}
        disabled={disabled}
        onEdit={() => setEditingField('position_note')}
        onCommit={(v) => {
          onUpdate({ ...record, position_note: v });
          setEditingField(null);
        }}
      />
      <NoteField
        label="Our angle — how we position against them"
        placeholder="How do we win against them?"
        value={record.our_angle ?? ''}
        editing={editingField === 'our_angle'}
        disabled={disabled}
        onEdit={() => setEditingField('our_angle')}
        onCommit={(v) => {
          onUpdate({ ...record, our_angle: v });
          setEditingField(null);
        }}
      />

      {!disabled && (
        <div className="flex flex-col gap-1.5">
          {/* The ONE action for the whole section. It TARGETS all four fields
              via POST /brand-bible/ai-suggest (section F). kind="text" renders
              each returned draft as its own separately-acceptable card, routed
              by stored target_field. name/domain are refuse-only. */}
          <AISuggest
            sectionId={competitorNotesSectionId(record.id)}
            kind="text"
            placeholder="Suggest notes"
            source="unverified — review each"
            fetcher={suggestFromModel}
            onAcceptText={acceptFieldDraft}
          />
          {fetchError && (
            <p role="alert" className="text-[11px] leading-snug text-[hsl(var(--status-error))]">
              {fetchError}
            </p>
          )}
          {refusals.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {refusals.map((r) => (
                <li key={r.field} className="text-[11px] leading-snug text-muted-foreground">
                  <span className="font-semibold">{COMPETITOR_FIELD_COVERAGE[r.field].label}:</span> {r.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-2.5">
        <button
          type="button"
          disabled={disabled || record.verified === true}
          onClick={() => onConfirm(record.id)}
          className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg))] px-2.5 py-1 text-[11.5px] font-semibold text-[hsl(var(--status-success))] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Check className="size-3" /> Confirm competitor
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onReject(record.id)}
          className="rounded-md border border-destructive px-2.5 py-1 text-[11.5px] font-semibold text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Not a competitor
        </button>
        <button
          type="button"
          disabled={disabled || !hasNextUnreviewed}
          onClick={() => onNextUnreviewed(record.id)}
          className="rounded-md border border-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next unreviewed
        </button>
      </div>
    </div>
  );
}

/* -------------------------------- BulkPasteModal ------------------------------- */

function BulkPasteModal({
  bulkText,
  setBulkText,
  preview,
  validCount,
  onCancel,
  onAdd,
}: {
  bulkText: string;
  setBulkText: (v: string) => void;
  preview: BulkPreviewItem[];
  validCount: number;
  onCancel: () => void;
  onAdd: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div onClick={onCancel} className="fixed inset-0 z-[80] grid place-items-center bg-foreground/30 backdrop-blur-sm">
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[82vh] w-[520px] max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        <div className="border-b border-border px-4 pb-3 pt-4">
          <div className="text-sm font-bold">Bulk add competitors</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">
            Paste a list — one per line, or comma-separated. We&apos;ll clean it up, skip duplicates, and flag
            anything that doesn&apos;t look right.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 overflow-auto p-3.5">
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Paste here
            </div>
            <textarea
              autoFocus
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={'Competitor name\nanother-competitor.com\nhttps://example-competitor.com'}
              rows={10}
              className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card px-3 py-2.5 font-mono text-[13px] outline-none focus:border-coral focus:ring-[3px] focus:ring-coral/15"
            />
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Preview ({validCount} new)
            </div>
            <div className="flex min-h-[200px] max-h-[260px] flex-col gap-1 overflow-auto rounded-lg border border-border bg-warm-bg p-2">
              {preview.length === 0 && <div className="p-2 text-[12px] text-muted-foreground">Paste to preview…</div>}
              {preview.map((item, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex items-center gap-1.5 rounded px-2 py-1',
                    item.status === 'valid'
                      ? 'border border-border bg-card opacity-100'
                      : 'border border-transparent bg-transparent opacity-55',
                  )}
                >
                  <span className="flex-1 truncate font-mono text-[12px]">{item.raw}</span>
                  {item.status === 'invalid' && <span className="text-[10px] text-destructive">invalid</span>}
                  {item.status === 'duplicate' && (
                    <span className="text-[10px] text-muted-foreground">already in the set</span>
                  )}
                  {item.status === 'self' && <span className="text-[10px] text-destructive">your own domain</span>}
                  {item.status === 'valid' && <Check className="size-3 text-[hsl(var(--status-success))]" />}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground hover:bg-warm-bg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAdd}
            disabled={validCount === 0}
            className={cn(
              'rounded-lg border px-3.5 py-2 text-[13px] font-semibold text-white',
              validCount === 0 ? 'cursor-not-allowed border-coral/50 bg-coral/50' : 'border-coral bg-coral hover:bg-coral-hover',
            )}
          >
            Add {validCount} new
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- CompetitiveSetSection ---------------------------- */

export function CompetitiveSetSection({
  value: valueProp,
  onChange: onChangeProp,
  disabled = false,
  tenantDomain,
  brandContext,
}: CompetitiveSetSectionProps) {
  // Rules of Hooks: this always runs. It's inert when unused — the hook's
  // own store reads/writes just no-op with no tenant loaded (see
  // brandBibleWizardStore.upsertEntry/discardEntry's own `if (!tenantId)`
  // guards) — and its result is only consulted below when the caller didn't
  // supply value/onChange itself.
  const persisted = useCompetitiveSet();
  const persistedOps: BulkReviewOps = persisted;
  const wizardTenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const tenantId = (tenantDomain ?? wizardTenantId ?? '').trim() || null;
  const isControlled = valueProp !== undefined;

  // Explicit-save buffer (persisted mode only): add / edit / remove stay
  // section-local until the section Save button commits them. Controlled mode
  // (explicit value/onChange) is untouched — the parent owns state and every
  // mutation flows straight out through onChange as before.
  const [buffer, setBuffer] = useState<CompetitorBuffer>(EMPTY_BUFFER);
  const bufferedRecords = useMemo(
    () => applyBuffer(persisted.records, buffer),
    [persisted.records, buffer],
  );

  const value = valueProp ?? bufferedRecords;
  const onChange = onChangeProp ?? persisted.onChange;
  const changeCount = bufferChangeCount(buffer);

  // The registry re-reads isDirty/changeCount only when notify() fires (on
  // register/save/discard). We recreate `registration` whenever changeCount
  // changes so the effect re-registers and notifies — driving the footer
  // "Save F · N changes" and the Unsaved indicator — while the methods
  // themselves read the LATEST buffer/commit through a ref so they are never
  // stale between re-registrations.
  const saveLatest = useRef({ buffer, commitBuffered: persisted.commitBuffered });
  saveLatest.current = { buffer, commitBuffered: persisted.commitBuffered };

  const registration = useMemo<BbSectionSaveRegistration | null>(() => {
    // Controlled mode: the parent owns state; §F must NOT register (it would
    // pollute the shared registry and claim a Save button it cannot honour).
    if (isControlled) return null;
    return {
      isDirty: () => bufferChangeCount(saveLatest.current.buffer) > 0,
      changeCount: () => bufferChangeCount(saveLatest.current.buffer),
      save: async (): Promise<DirtyFormSaveResult> => {
        const b = saveLatest.current.buffer;
        if (bufferChangeCount(b) === 0) return { success: true };
        const ops: CompetitiveBufferedCommit = {
          added: b.added,
          edited: Object.values(b.edits),
          removedIds: b.removed,
        };
        try {
          const ok = await saveLatest.current.commitBuffered(ops);
          if (!ok) {
            // A failed commit MUST leave the buffer intact — never silently
            // drop buffered edits.
            return { success: false, error: 'Some competitor changes could not be saved.' };
          }
          setBuffer(EMPTY_BUFFER);
          return { success: true };
        } catch (err) {
          return {
            success: false,
            error: err instanceof Error ? err.message : 'Failed to save competitors.',
          };
        }
      },
      discard: () => setBuffer(EMPTY_BUFFER),
    };
  }, [isControlled, changeCount]);

  useRegisterBbSectionSave('F', registration);

  const [draft, setDraft] = useState('');
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Shift-click range anchor: the last chip whose checkbox was plain-clicked,
  // plus the group/filter context it was clicked in. A range is only ever
  // computed when the shift-click lands in the SAME group under the SAME
  // filter (review-model item 3) — otherwise it degrades to a plain toggle.
  const [anchor, setAnchor] = useState<{ id: string; group: GroupKey; filter: FilterKey } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedTenant = normalizeTenantDomain(tenantDomain);

  const commitAdd = (raw: string) => {
    const parsed = parseQuickAdd(raw);
    if (!parsed) return;
    if (parsed.domain && isSelfDomain(parsed.domain, normalizedTenant)) {
      setDuplicateNotice(`${cleanDomain(parsed.domain)} is your own domain, not a competitor.`);
      return;
    }
    const key = dedupeKey(parsed);
    if (value.some((r) => dedupeKey(r) === key)) {
      setDuplicateNotice(`${parsed.name} is already in the set`);
      return;
    }
    setDuplicateNotice(null);
    const record: CompetitorRecord = {
      id: makeId(),
      name: parsed.name,
      domain: parsed.domain,
      public_mention: false,
      verified: false,
      source: 'client_edit',
    };
    addRecords([record]);
    setDraft('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) commitAdd(draft);
    }
    // Backspace is intentionally left alone here: an empty add row must never
    // delete the last record — these are content-bearing records, not chips.
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const text = e.clipboardData.getData('text');
    const lines = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (lines.length > 1) {
      e.preventDefault();
      setBulkText(text);
      setBulkOpen(true);
    }
  };

  // Add / edit / remove are EDITS: in persisted mode they buffer id-keyed and
  // nothing reaches the store until Save; in controlled mode they flow straight
  // out through onChange exactly as before. Confirm/Reject are handled
  // separately (confirmRecords/rejectRecords) and stay immediate in both modes.
  const addRecords = (recs: CompetitorRecord[]) => {
    if (recs.length === 0) return;
    if (isControlled) {
      onChange([...value, ...recs]);
      return;
    }
    // Temp ids from makeId() never collide with a store row, so appending is safe.
    setBuffer((b) => ({ ...b, added: [...b.added, ...recs] }));
  };

  const updateRecord = (next: CompetitorRecord) => {
    if (isControlled) {
      onChange(value.map((r) => (r.id === next.id ? next : r)));
      return;
    }
    setBuffer((b) => {
      // A buffered add edited before Save updates in place — still just a create.
      if (b.added.some((r) => r.id === next.id)) {
        return { ...b, added: b.added.map((r) => (r.id === next.id ? next : r)) };
      }
      // An existing store row: buffer the desired next record, keyed by its id.
      return { ...b, edits: { ...b.edits, [next.id]: next } };
    });
  };

  const removeRecord = (id: string) => {
    if (isControlled) {
      onChange(value.filter((r) => r.id !== id));
      return;
    }
    setBuffer((b) => {
      // Removing a buffered add just drops it — no store row exists to delete.
      if (b.added.some((r) => r.id === id)) {
        return { ...b, added: b.added.filter((r) => r.id !== id) };
      }
      // Existing store row: drop any pending edit and mark this EXACT id removed
      // (id-keyed — NEVER a list-diff delete). Idempotent on a repeated remove.
      const { [id]: _dropped, ...restEdits } = b.edits;
      return {
        ...b,
        edits: restEdits,
        removed: b.removed.includes(id) ? b.removed : [...b.removed, id],
      };
    });
  };

  /**
   * Confirm, single or bulk. Persisted mode delegates to the hook's
   * `confirmMany` (the interface contract with useCompetitiveSet.ts);
   * controlled mode expresses the same intent through `onChange` by stamping
   * `verified`/`last_verified` on the targeted records — the only fields the
   * presentational record shape has for review state.
   */
  const confirmRecords = (ids: string[]) => {
    if (ids.length === 0) return;
    if (!isControlled && persistedOps.confirmMany) {
      persistedOps.confirmMany(ids);
      return;
    }
    const idSet = new Set(ids);
    const now = new Date().toISOString();
    onChange(value.map((r) => (idSet.has(r.id) ? { ...r, verified: true, last_verified: now } : r)));
  };

  /**
   * Reject, single or bulk — first-class, never silently equal to a plain
   * delete in persisted mode: the hook's `rejectMany` owns what a rejection
   * durably persists (see useCompetitiveSet.ts). Controlled mode has no
   * rejected field to set, so it falls back to removal through `onChange`,
   * which is also exactly what the x button does — documented in the file
   * header (review-model item 6).
   */
  const rejectRecords = (ids: string[]) => {
    if (ids.length === 0) return;
    if (!isControlled && persistedOps.rejectMany) {
      persistedOps.rejectMany(ids);
      return;
    }
    const idSet = new Set(ids);
    onChange(value.filter((r) => !idSet.has(r.id)));
  };

  /* ----------------------- grouping / filtering / selection ----------------------- */

  const reviewCount = useMemo(() => value.filter((r) => r.verified !== true).length, [value]);
  const confirmedCount = value.length - reviewCount;

  /** Records visible under the current filter, grouped by source, original order kept. */
  const visibleByGroup = useMemo(() => {
    const grouped: Record<GroupKey, CompetitorRecord[]> = { suggested: [], person: [] };
    for (const r of value) {
      if (matchesFilter(r, filter)) grouped[groupOf(r)].push(r);
    }
    return grouped;
  }, [value, filter]);

  /** Flat visible ordering (group order, then record order) — drives "Next unreviewed". */
  const visibleOrdered = useMemo(() => GROUP_ORDER.flatMap((g) => visibleByGroup[g]), [visibleByGroup]);

  // Selection state can hold ids whose records have since been removed or
  // rejected; the LIVE count the bulk bar shows is always the intersection
  // with the records that actually exist right now.
  const liveSelected = useMemo(() => {
    const existing = new Set(value.map((r) => r.id));
    return new Set([...selected].filter((id) => existing.has(id)));
  }, [selected, value]);
  const liveSelectedCount = liveSelected.size;

  const handleSlotClick = (e: React.MouseEvent, record: CompetitorRecord) => {
    const group = groupOf(record);
    if (e.shiftKey && anchor && anchor.group === group && anchor.filter === filter) {
      const list = visibleByGroup[group];
      const a = list.findIndex((r) => r.id === anchor.id);
      const b = list.findIndex((r) => r.id === record.id);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = list.slice(lo, hi + 1).map((r) => r.id);
        setSelected((prev) => new Set([...prev, ...range]));
        return;
      }
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(record.id)) next.delete(record.id);
      else next.add(record.id);
      return next;
    });
    setAnchor({ id: record.id, group, filter });
  };

  const toggleExpand = (record: CompetitorRecord) => {
    setExpandedId((prev) => (prev === record.id ? null : record.id));
  };

  /** "Next unreviewed": the next unconfirmed record in the visible ordering, wrapping around. */
  const nextUnreviewedFrom = (fromId: string): string | null => {
    const idx = visibleOrdered.findIndex((r) => r.id === fromId);
    const rest = idx === -1 ? visibleOrdered : [...visibleOrdered.slice(idx + 1), ...visibleOrdered.slice(0, idx)];
    return rest.find((r) => r.verified !== true)?.id ?? null;
  };
  const hasNextUnreviewed = visibleOrdered.some((r) => r.verified !== true);

  const clearSelection = () => setSelected(new Set());

  const confirmSelected = () => {
    const ids = [...liveSelected];
    confirmRecords(ids);
    clearSelection();
  };
  const rejectSelected = () => {
    const ids = [...liveSelected];
    rejectRecords(ids);
    clearSelection();
  };

  const bulkPreview = useMemo(
    () => buildBulkPreview(bulkText, value, normalizedTenant),
    [bulkText, value, normalizedTenant],
  );
  const validCount = bulkPreview.filter((b) => b.status === 'valid').length;

  const closeBulk = () => {
    setBulkOpen(false);
    setBulkText('');
  };

  const handleBulkAdd = () => {
    const additions: CompetitorRecord[] = bulkPreview
      .filter((b): b is BulkPreviewItem & { parsed: QuickCandidate } => b.status === 'valid' && b.parsed !== null)
      .map((b) => ({
        id: makeId(),
        name: b.parsed.name,
        domain: b.parsed.domain,
        public_mention: false,
        verified: false,
        source: 'client_edit',
      }));
    if (additions.length) addRecords(additions);
    closeBulk();
  };

  const FILTER_PILLS: { key: FilterKey; label: string; count: number }[] = [
    { key: 'review', label: 'Needs review', count: reviewCount },
    { key: 'confirmed', label: 'Confirmed', count: confirmedCount },
    { key: 'all', label: 'All', count: value.length },
  ];

  return (
    <div className="rounded-2xl border border-border bg-card px-6 py-5 shadow-sm">
      <div className="mb-3.5 flex flex-wrap items-center gap-3">
        <div className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-lg border border-border bg-warm-bg font-mono text-[13px] font-semibold text-coral">
          F
        </div>
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-semibold -tracking-[0.01em]">Competitive Set</h2>
          <span className="rounded-[5px] border border-border bg-warm-bg px-1.5 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Internal only
          </span>
        </div>
        <span className="ml-auto rounded-md bg-teal-light px-2 py-1 font-mono text-[11px] text-teal">
          {value.length} competitor{value.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mb-3.5 rounded-lg border border-[hsl(var(--status-warning)/0.3)] bg-[hsl(var(--status-warning-bg))] px-3.5 py-2.5 text-[12px] text-[hsl(var(--status-warning))]">
        <strong className="font-semibold">Internal intelligence.</strong> Agents use this to shape strategy and
        positioning — they never name a competitor in public copy. Each entry defaults to{' '}
        <strong className="font-semibold">public mention: off</strong>.
      </div>

      <div className="mb-1.5 flex items-center gap-2">
        <div
          className={cn(
            'flex flex-1 items-center rounded-lg border border-border bg-card px-2.5 transition-[border-color,box-shadow] duration-150',
            'focus-within:border-coral focus-within:ring-[3px] focus-within:ring-coral/15',
          )}
        >
          <input
            ref={inputRef}
            value={draft}
            disabled={disabled}
            onChange={(e) => {
              setDraft(e.target.value);
              if (duplicateNotice) setDuplicateNotice(null);
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder="Add a competitor — name or domain, Enter to add. Paste a list to bulk-add."
            className="flex-1 border-0 bg-transparent py-2 text-[13.5px] outline-none disabled:cursor-not-allowed"
          />
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setBulkOpen(true)}
          className="inline-flex flex-none items-center gap-1 rounded-md border border-border bg-transparent px-2.5 py-2 text-[11.5px] font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Clipboard className="size-3" /> Bulk paste
        </button>
      </div>
      {duplicateNotice && (
        <p role="alert" className="mb-3.5 text-[11.5px] text-[hsl(var(--status-warning))]">
          {duplicateNotice}
        </p>
      )}

      {value.length === 0 ? (
        <p className="mt-3.5 text-[12.5px] text-muted-foreground">
          Add the competitors you actually lose work to — this stays internal.
        </p>
      ) : (
        <>
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {FILTER_PILLS.map((pill) => (
              <button
                key={pill.key}
                type="button"
                aria-pressed={filter === pill.key}
                onClick={() => setFilter(pill.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium',
                  filter === pill.key
                    ? 'border-coral/40 bg-coral-light text-coral-dark'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground',
                )}
              >
                {pill.label}
                <span className="font-mono text-[10.5px]">{pill.count}</span>
              </button>
            ))}
          </div>

          {liveSelectedCount > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2 rounded-lg border border-coral/40 bg-coral-light px-3 py-2">
              <span className="text-[12px] font-semibold text-coral-dark">{liveSelectedCount} selected</span>
              <button
                type="button"
                disabled={disabled}
                onClick={confirmSelected}
                className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg))] px-2.5 py-1 text-[11.5px] font-semibold text-[hsl(var(--status-success))] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Check className="size-3" /> Confirm {liveSelectedCount}
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={rejectSelected}
                className="rounded-md border border-destructive px-2.5 py-1 text-[11.5px] font-semibold text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Not competitors ({liveSelectedCount})
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto text-[11.5px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}

          {GROUP_ORDER.map((groupKey) => {
            const records = visibleByGroup[groupKey];
            return (
              <div key={groupKey} className="mt-3.5">
                <div className="mb-1.5 flex items-baseline gap-2">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {GROUP_LABEL[groupKey]}
                  </h3>
                  <span className="font-mono text-[10.5px] text-muted-foreground">{records.length}</span>
                </div>
                {records.length === 0 ? (
                  <p className="text-[11.5px] italic text-muted-foreground">
                    Nothing in this group under the current filter.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-start gap-1.5">
                    {records.map((record) => (
                      <Fragment key={record.id}>
                        <CompetitorChip
                          record={record}
                          selected={liveSelected.has(record.id)}
                          expanded={expandedId === record.id}
                          disabled={disabled}
                          onSlotClick={handleSlotClick}
                          onToggleExpand={toggleExpand}
                          onRemove={removeRecord}
                        />
                        {expandedId === record.id && (
                          <ExpandedRecordPanel
                            record={record}
                            disabled={disabled}
                            brandContext={brandContext}
                            tenantId={tenantId}
                            hasNextUnreviewed={hasNextUnreviewed}
                            onUpdate={updateRecord}
                            onConfirm={(id) => confirmRecords([id])}
                            onReject={(id) => rejectRecords([id])}
                            onNextUnreviewed={(fromId) => {
                              const next = nextUnreviewedFrom(fromId);
                              if (next) setExpandedId(next);
                            }}
                          />
                        )}
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {bulkOpen && (
        <BulkPasteModal
          bulkText={bulkText}
          setBulkText={setBulkText}
          preview={bulkPreview}
          validCount={validCount}
          onCancel={closeBulk}
          onAdd={handleBulkAdd}
        />
      )}
    </div>
  );
}
